/**
 * ReminderModal — create or edit a reminder.
 *
 * Features:
 *   • Quick presets: "In 1 hour", "Today 6 PM", "Tomorrow 9 AM", "Next Monday 9 AM".
 *   • Manual date/time pickers.
 *   • Voice input button — hold, speak "remind me tomorrow 5 pm to call Raju",
 *     backend parses via Claude, fields pre-fill.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Save, Sparkles, Clock, CalendarCheck } from 'lucide-react';
import VoiceInput from './VoiceInput';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// ─── Date helpers (IST localized datetime-local string `YYYY-MM-DDTHH:mm`)
function toLocalInput(d) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function nextAt(hour, minute = 0, whichDay = 0) {
  // whichDay: 0 = today (or tomorrow if passed), 1 = tomorrow, 'nextMonday'
  const d = new Date();
  if (whichDay === 'nextMonday') {
    const offset = ((1 - d.getDay() + 7) % 7) || 7; // never today
    d.setDate(d.getDate() + offset);
  } else if (whichDay === 1) {
    d.setDate(d.getDate() + 1);
  } else {
    // today — push to tomorrow if time already passed
    const candidate = new Date(d);
    candidate.setHours(hour, minute, 0, 0);
    if (candidate <= d) d.setDate(d.getDate() + 1);
  }
  d.setHours(hour, minute, 0, 0);
  return d;
}

export default function ReminderModal({ editing, onClose, onSaved }) {
  const initial = (() => {
    if (editing) {
      return {
        title: editing.title || '',
        body: editing.body || '',
        when: toLocalInput(new Date(editing.fire_at)),
        link: editing.link || '',
      };
    }
    const d = new Date();
    d.setMinutes(d.getMinutes() + 60);
    d.setSeconds(0);
    return { title: '', body: '', when: toLocalInput(d), link: '' };
  })();
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [voiceParsing, setVoiceParsing] = useState(false);
  const [google, setGoogle] = useState(null);
  const [sync, setSync] = useState({ calendar: false, tasks: false });

  useEffect(() => {
    if (editing) return;
    axios.get(`${API}/google/status`, { withCredentials: true }).then((r) => {
      setGoogle(r.data);
      if (r.data.connected) setSync({ calendar: !!r.data.prefs?.calendar, tasks: !!r.data.prefs?.tasks });
    }).catch(() => {});
  }, [editing]);

  const applyPreset = (presetDate) => {
    setForm((f) => ({ ...f, when: toLocalInput(presetDate) }));
  };

  const handleVoice = async (text) => {
    setVoiceParsing(true);
    try {
      const res = await axios.post(`${API}/reminders/voice-parse`, { text }, { withCredentials: true });
      const fa = new Date(res.data.fire_at);
      setForm((f) => ({
        ...f,
        title: res.data.title || text,
        body: res.data.body || '',
        when: toLocalInput(fa),
      }));
      toast.success(`Parsed: "${res.data.title}" @ ${fa.toLocaleString('en-IN')}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not parse. Fill manually.');
      setForm((f) => ({ ...f, title: text }));
    } finally {
      setVoiceParsing(false);
    }
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error('Title required'); return; }
    // Parse local datetime-local string as LOCAL time (not UTC)
    const local = new Date(form.when);
    if (isNaN(local.getTime())) { toast.error('Invalid date/time'); return; }
    if (local <= new Date()) { toast.error('Time must be in the future'); return; }
    setSaving(true);
    try {
      const payload = { ...form, fire_at: local.toISOString() };
      if (editing) {
        await axios.patch(`${API}/reminders/${editing.id}`, payload, { withCredentials: true });
        toast.success('Reminder updated');
      } else {
        const res = await axios.post(`${API}/reminders`, { ...payload, google: sync }, { withCredentials: true });
        const where = [res.data.google_event_id && 'Google Calendar', res.data.google_task_id && 'Google Tasks'].filter(Boolean);
        toast.success(where.length ? `Reminder set · also in ${where.join(' + ')}` : 'Reminder set');
        if (res.data.google_error) toast.error(`Google sync: ${res.data.google_error}`);
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="reminder-modal">
        <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between">
          <h3 className="font-semibold text-zinc-50 text-sm flex items-center gap-2">
            <Clock size={14} className="text-amber-400" /> {editing ? 'Edit Reminder' : 'New Reminder'}
          </h3>
          <button onClick={onClose}><X size={18} className="text-zinc-500 hover:text-zinc-200" /></button>
        </div>
        <div className="p-5 space-y-4">
          {/* Voice entry */}
          <div className="flex items-center gap-2 p-3 rounded-xl bg-blue-900/20 border border-blue-800/40">
            <VoiceInput onTranscript={handleVoice} context="reminder" size={14} title='Speak: "remind me tomorrow 5 pm to call Raju"' />
            <span className="text-xs text-zinc-300 flex-1">Or tap mic and say <span className="text-amber-300">"remind me tomorrow 5 pm to call Raju"</span></span>
            {voiceParsing && <Sparkles size={14} className="text-amber-400 animate-pulse" />}
          </div>

          {/* Title */}
          <div>
            <label className="text-zinc-400 text-xs mb-1 block">Title</label>
            <input
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="e.g. Call Raju about packaging"
              className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500"
              data-testid="reminder-title-input"
            />
          </div>

          {/* Body */}
          <div>
            <label className="text-zinc-400 text-xs mb-1 block">Note (optional)</label>
            <textarea
              rows={2}
              value={form.body}
              onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
              placeholder="Short context"
              className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500 resize-none"
              data-testid="reminder-body-input"
            />
          </div>

          {/* Quick presets */}
          <div>
            <label className="text-zinc-400 text-xs mb-1.5 block">Quick set</label>
            <div className="grid grid-cols-2 gap-1.5">
              {[
                ['In 1 hour', () => { const d = new Date(); d.setMinutes(d.getMinutes() + 60); d.setSeconds(0); return d; }],
                ['Today 6 PM', () => nextAt(18)],
                ['Tomorrow 9 AM', () => nextAt(9, 0, 1)],
                ['Next Monday 9 AM', () => nextAt(9, 0, 'nextMonday')],
              ].map(([label, fn]) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => applyPreset(fn())}
                  className="text-xs py-2 px-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 border border-zinc-700"
                  data-testid={`preset-${label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Manual date/time */}
          <div>
            <label className="text-zinc-400 text-xs mb-1 block">Alarm time</label>
            <input
              type="datetime-local"
              value={form.when}
              onChange={(e) => setForm((f) => ({ ...f, when: e.target.value }))}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500"
              data-testid="reminder-when-input"
            />
          </div>

          {/* Google sync */}
          {!editing && google?.connected && (
            <div className="flex flex-wrap items-center gap-2 p-3 rounded-xl bg-emerald-900/10 border border-emerald-800/40" data-testid="reminder-google-sync">
              <CalendarCheck size={13} className="text-emerald-400" />
              <span className="text-[11px] text-zinc-400 mr-1">Also add to</span>
              {[['calendar', 'Google Calendar'], ['tasks', 'Google Tasks']].map(([k, label]) => (
                <button key={k} type="button" onClick={() => setSync((s) => ({ ...s, [k]: !s[k] }))}
                  className={`text-[11px] px-2.5 py-1 rounded-lg border ${sync[k] ? 'bg-emerald-500/15 border-emerald-700 text-emerald-300' : 'bg-zinc-800 border-zinc-700 text-zinc-400'}`}
                  data-testid={`reminder-sync-${k}`}>
                  {sync[k] ? '✓ ' : ''}{label}
                </button>
              ))}
            </div>
          )}

          {/* Save */}
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-zinc-700 text-zinc-400 text-sm">Cancel</button>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="flex-1 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-semibold text-sm flex items-center justify-center gap-1.5 disabled:bg-zinc-700"
              data-testid="reminder-save-btn"
            >
              <Save size={13} /> {saving ? 'Saving…' : editing ? 'Update' : 'Set Alarm'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
